/**
 * ============================================================================
 * ItemTraceabilityModal — Barrera 3: Recibo de Trazabilidad Imprimible
 * ============================================================================
 *
 * Recibo inmutable por ítem que muestra el desglose completo de Landed Cost:
 *   - Base Gravable DIAN (FOB + Fletes Internacionales gravables)
 *   - Desglose detallado de cada gasto asignado al ítem (TRACE-01)
 *   - Arancel
 *   - IVA de importación (descontable, NO capitaliza)
 *   - Costo nacionalizado unitario
 *
 * TRACE-01: Las allocations vienen pre-calculadas por el RPC. El frontend
 * NO hace matemática — solo agrupa por `baseDeclarada` (CBM/Peso/Valor) y
 * suma los subtotales. Garantía del motor:
 *   SUM(allocations.montoCOP) = (CIF − FOB) del ítem al céntimo exacto.
 *
 * Fuentes de datos:
 *   - Si `embarque.liquidado === true` → snapshot congelado (sin desglose).
 *   - Si NO está liquidado → motor vivo + allocations granulares.
 * ============================================================================
 */
import { useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Lock, Printer, X, Box, Scale, DollarSign, Info, Wine } from "lucide-react";
import type { InventoryItem } from "@/hooks/useDocuments";
import type { GastoEmbarque } from "@/hooks/useGastosEmbarque";
import type { AllocationLine } from "@/hooks/useShipmentLandedCost";

const copFmt = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export interface TraceabilityLivePerItem {
  fobItemCOP?: number;
  fleteAsignado?: number;
  seguroAsignado?: number;
  /** @deprecated TRACE-01 — usar `allocations` para detalle por gasto. */
  seguroIntAsignado?: number;
  /** @deprecated TRACE-01 — usar `allocations` para detalle por gasto. */
  otrosValorAsignados?: number;
  otrosAsignados?: number;
  arancelItemCOP?: number;
  ivaItemCOP?: number;
  /** ICO (Impuesto al Consumo) capitalizado al ítem. 0 si no aplica. */
  icoCOP?: number;
  landedCostUnitarioCOP?: number | null;
  landedCostTotalCOP?: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: InventoryItem | null;
  /** True ⇒ datos congelados desde base_inventario; False ⇒ motor vivo */
  isLiquidado: boolean;
  /** Datos del motor vivo (solo cuando NO está liquidado) */
  livePerItem?: TraceabilityLivePerItem | null;
  /** TRM aduanera usada para el cálculo (informativo) */
  trmAduana?: number | null;
  /** Numero DO del embarque (header del recibo) */
  numeroDo?: string | null;
  /** TRACE-01 — todos los gastos del embarque (para resolver concepto/moneda por gasto_id). */
  gastos?: GastoEmbarque[];
  /** TRACE-01 — allocations YA filtradas para este ítem específico. */
  allocations?: AllocationLine[];
}

/** Moneda original formateada (con o sin decimales según convención). */
function formatOriginal(monto: number, moneda: string): string {
  const m = (moneda ?? "COP").toUpperCase();
  if (m === "COP") {
    return copFmt.format(monto);
  }
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: m,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(monto);
}

export function ItemTraceabilityModal({
  open,
  onOpenChange,
  item,
  isLiquidado,
  livePerItem,
  trmAduana,
  numeroDo,
  gastos = [],
  allocations = [],
}: Props) {
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("printing-enabled");
    return () => document.body.classList.remove("printing-enabled");
  }, [open]);

  // ── Resolver fuente de datos ──
  const isFrozen = isLiquidado;
  const cantidad = Number(item?.cantidad) || 0;

  const frozenLandedUnit = Number((item as any)?.costo_nacionalizado_unitario_cop ?? 0);
  const frozenIvaTotal = Number((item as any)?.valor_iva_cop ?? 0);
  const frozenArancelTotal = Number((item as any)?.valor_arancel_cop ?? 0);

  const liveFob = Number(livePerItem?.fobItemCOP ?? 0);
  const liveArancel = Number(livePerItem?.arancelItemCOP ?? 0);
  const liveIva = Number(livePerItem?.ivaItemCOP ?? 0);
  const liveLandedUnit = Number(livePerItem?.landedCostUnitarioCOP ?? 0);

  const landedUnit = isFrozen ? frozenLandedUnit : liveLandedUnit;
  const landedTotal = landedUnit * cantidad;
  const arancelShow = isFrozen ? frozenArancelTotal : liveArancel;
  const ivaShow = isFrozen ? frozenIvaTotal : liveIva;
  // ICO: sólo motor vivo (no se persiste snapshot por ítem aún).
  const liveIco = Number(livePerItem?.icoCOP ?? 0);
  const icoShow = isFrozen ? 0 : liveIco;

  // ─────────────────────────────────────────────────────────────────────
  // TRACE-01: Agrupar allocations por baseDeclarada y resolver concepto/moneda
  // ─────────────────────────────────────────────────────────────────────
  const gastosById = useMemo(() => {
    const map = new Map<string, GastoEmbarque>();
    for (const g of gastos) map.set(g.id, g);
    return map;
  }, [gastos]);

  type GrupoLinea = {
    gastoId: string | null;
    concepto: string;
    montoOriginal: number | null;
    monedaOriginal: string | null;
    montoCOP: number;
  };

  const grupos = useMemo(() => {
    const acc: Record<"CBM" | "Peso" | "Valor", GrupoLinea[]> = {
      CBM: [],
      Peso: [],
      Valor: [],
    };
    for (const a of allocations) {
      const g = a.gastoId ? gastosById.get(a.gastoId) : undefined;
      acc[a.baseDeclarada].push({
        gastoId: a.gastoId,
        concepto: g?.concepto ?? "(gasto sin nombre)",
        montoOriginal: g ? Number(g.monto_base ?? g.monto ?? 0) : null,
        monedaOriginal: g?.moneda ?? null,
        montoCOP: a.montoCOP,
      });
    }
    return acc;
  }, [allocations, gastosById]);

  const subtotalCBM = grupos.CBM.reduce((s, l) => s + l.montoCOP, 0);
  const subtotalPeso = grupos.Peso.reduce((s, l) => s + l.montoCOP, 0);
  const subtotalValor = grupos.Valor.reduce((s, l) => s + l.montoCOP, 0);
  const totalGastosAsignados = subtotalCBM + subtotalPeso + subtotalValor;

  const baseGravable = isFrozen
    ? Math.max(0, landedTotal - arancelShow) // approx para snapshot legacy
    : liveFob + totalGastosAsignados;

  const hasAllocations = allocations.length > 0 && !isFrozen;

  // ── Desglose para tooltip CFO ──
  const ivaUnitario = cantidad > 0 ? ivaShow / cantidad : 0;
  const totalCajaUnitario = landedUnit + ivaUnitario;
  const totalCajaTotal = landedTotal + ivaShow;

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl print-area max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">Recibo de Trazabilidad — {item.sku ?? item.descripcion}</DialogTitle>

        {/* === Header (oculto en impresión) === */}
        <div className="flex items-center justify-between no-print">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Trazabilidad de Costo</h2>
            {isFrozen ? (
              <Badge variant="default" className="text-[10px] gap-1">
                <Lock className="h-3 w-3" /> Liquidado · Inmutable
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px]">Motor vivo</Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => window.print()}>
              <Printer className="h-3.5 w-3.5" /> Imprimir
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* === Recibo === */}
        <div className="receipt mt-4 space-y-4 text-sm">
          {/* Header del recibo */}
          <div className="border-b pb-3 print-only-block">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Embarque</p>
            <p className="font-semibold">{numeroDo ?? "—"}</p>
            <div className="mt-1 grid grid-cols-2 gap-x-4 text-[11px]">
              <div>
                <span className="text-muted-foreground">SKU:</span>{" "}
                <span className="font-mono">{item.sku ?? "—"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">No. Doc:</span>{" "}
                <span className="font-mono">{item.no_documento ?? "—"}</span>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Descripción:</span>{" "}
                <span>{item.descripcion ?? "—"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Cantidad:</span>{" "}
                <span className="tabular-nums">{cantidad.toLocaleString("es-CO")}</span>
              </div>
              <div>
                <span className="text-muted-foreground">TRM Aduana:</span>{" "}
                <span className="tabular-nums">{trmAduana ? copFmt.format(trmAduana) : "—"}</span>
              </div>
            </div>
          </div>

          {/* Sección 1: Base Gravable */}
          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              1. Base Gravable DIAN
            </h3>
            {!isFrozen && (
              <>
                <Row label="FOB del ítem (COP)" value={liveFob} muted />
                <Row label="Gastos capitalizados al ítem" value={totalGastosAsignados} muted />
                <Separator className="my-1" />
              </>
            )}
            <Row label="Base Gravable" value={baseGravable} bold />
          </section>

          {/* TRACE-01: Sección 1.1 — Desglose por gasto */}
          {hasAllocations && (
            <section className="space-y-2 rounded-md border border-border/50 bg-muted/30 p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Gastos asignados a este producto
                </h3>
                <span className="text-[10px] text-muted-foreground italic">
                  {allocations.length} asignaciones
                </span>
              </div>

              {/* Grupo CBM */}
              {grupos.CBM.length > 0 && (
                <GrupoBlock
                  icon={<Box className="h-3.5 w-3.5" />}
                  titulo="Gastos por Volumen (CBM)"
                  lineas={grupos.CBM}
                  subtotal={subtotalCBM}
                  notaCBM
                />
              )}

              {/* Grupo Peso */}
              {grupos.Peso.length > 0 && (
                <GrupoBlock
                  icon={<Scale className="h-3.5 w-3.5" />}
                  titulo="Gastos por Peso"
                  lineas={grupos.Peso}
                  subtotal={subtotalPeso}
                />
              )}

              {/* Grupo Valor */}
              {grupos.Valor.length > 0 && (
                <GrupoBlock
                  icon={<DollarSign className="h-3.5 w-3.5" />}
                  titulo="Gastos por Valor (FOB)"
                  lineas={grupos.Valor}
                  subtotal={subtotalValor}
                />
              )}

              <Separator className="my-1" />
              <div className="flex items-center justify-between font-semibold">
                <span className="text-xs">Total gastos asignados al ítem</span>
                <span className="tabular-nums text-sm">{copFmt.format(totalGastosAsignados)}</span>
              </div>
              <p className="text-[10px] text-muted-foreground italic leading-snug">
                Garantía del motor: la suma de cada grupo cuadra al céntimo con los pools
                totales del embarque (algoritmo Largest Remainder en el RPC).
              </p>
            </section>
          )}

          {/* Sección 2: Arancel */}
          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              2. Arancel
            </h3>
            <Row label="Arancel a pagar (sobre base gravable)" value={arancelShow} bold />
          </section>

          {/* Sección 3: IVA */}
          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              3. IVA de Importación
            </h3>
            <Row label="IVA (descontable, NO capitaliza)" value={ivaShow} bold />
            <p className="text-[10px] text-muted-foreground italic leading-snug">
              NIC 2: el IVA de importación se trata como impuesto recuperable y NO forma parte
              del costo del inventario. Se separa para conciliación tributaria.
            </p>
          </section>

          {/* Sección 3.1: ICO (sólo si aplica) */}
          {icoShow > 0 && (
            <section className="space-y-1.5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Wine className="h-3.5 w-3.5" />
                Impuesto al Consumo (ICO)
              </h3>
              <Row label="ICO capitalizado al ítem" value={icoShow} bold />
              <p className="text-[10px] text-muted-foreground italic leading-snug">
                Impuesto al Consumo calculado por el motor según tipo de producto
                (licores, vehículos, cigarrillos, etc.). A diferencia del IVA, el ICO
                SÍ forma parte del costo del inventario (NIC 2).
              </p>
            </section>
          )}

          <Separator />

          {/* Sección 4: Resultado */}
          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              4. Costo Nacionalizado (NIC 2)
            </h3>
            <TooltipProvider delayDuration={150}>
              <div className="flex items-center justify-between font-semibold text-primary">
                <span className="text-xs">Costo unitario</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="tabular-nums text-sm cursor-help underline decoration-dotted decoration-muted-foreground/50">
                      {copFmt.format(landedUnit || 0)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="left" align="center" sideOffset={8} collisionPadding={16} className="w-[300px]">
                    <div className="space-y-1 text-[11px]">
                      <div className="flex justify-between gap-4">
                        <span>Costo inventario (sin IVA)</span>
                        <span className="tabular-nums font-medium">{copFmt.format(landedUnit || 0)}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>IVA descontable (19%)</span>
                        <span className="tabular-nums font-medium">{copFmt.format(ivaUnitario)}</span>
                      </div>
                      <div className="border-t border-border/40 pt-1 mt-1 flex justify-between gap-4 font-semibold">
                        <span>Total salida de caja</span>
                        <span className="tabular-nums">{copFmt.format(totalCajaUnitario)}</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center justify-between font-semibold text-primary">
                <span className="text-xs">{`Costo total × ${cantidad.toLocaleString("es-CO")} und`}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="tabular-nums text-sm cursor-help underline decoration-dotted decoration-muted-foreground/50">
                      {copFmt.format(landedTotal || 0)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="left" align="center" sideOffset={8} collisionPadding={16} className="w-[300px]">
                    <div className="space-y-1 text-[11px]">
                      <div className="flex justify-between gap-4">
                        <span>Costo inventario (sin IVA)</span>
                        <span className="tabular-nums font-medium">{copFmt.format(landedTotal || 0)}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>IVA descontable (19%)</span>
                        <span className="tabular-nums font-medium">{copFmt.format(ivaShow)}</span>
                      </div>
                      <div className="border-t border-border/40 pt-1 mt-1 flex justify-between gap-4 font-semibold">
                        <span>Total salida de caja</span>
                        <span className="tabular-nums">{copFmt.format(totalCajaTotal)}</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </section>

          {/* Pie del recibo */}
          <div className="border-t pt-2 print-only-block text-[10px] text-muted-foreground leading-snug">
            <p>
              Generado el {new Date().toLocaleString("es-CO")} ·{" "}
              {isFrozen
                ? "Datos congelados desde snapshot inmutable de auditoría DIAN."
                : "Cálculo del motor vivo — sujeto a cambios hasta liquidación."}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componentes
// ─────────────────────────────────────────────────────────────────────────────

function GrupoBlock({
  icon,
  titulo,
  lineas,
  subtotal,
  notaCBM,
}: {
  icon: React.ReactNode;
  titulo: string;
  lineas: Array<{
    gastoId: string | null;
    concepto: string;
    montoOriginal: number | null;
    monedaOriginal: string | null;
    montoCOP: number;
  }>;
  subtotal: number;
  notaCBM?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        {icon}
        <span>{titulo}</span>
        {notaCBM && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px]">
                <p className="text-[11px] leading-snug">
                  El motor de Landed Cost prorratea estos gastos usando el factor{" "}
                  <strong>Valor</strong> del ítem. La etiqueta CBM refleja la intención del
                  usuario al registrar el gasto. Próxima versión del motor agregará prorrateo
                  por CBM nativo.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <ul className="ml-5 space-y-0.5">
        {lineas.map((l, idx) => (
          <li
            key={`${l.gastoId ?? "anon"}-${idx}`}
            className="flex items-baseline justify-between gap-2 text-[11px]"
          >
            <span className="text-muted-foreground truncate">
              ├── {l.concepto}
              {l.montoOriginal != null && l.monedaOriginal && l.monedaOriginal.toUpperCase() !== "COP" && (
                <span className="text-[10px] opacity-70">
                  {" "}({formatOriginal(l.montoOriginal, l.monedaOriginal)})
                </span>
              )}
            </span>
            <span className="tabular-nums shrink-0">{copFmt.format(l.montoCOP)}</span>
          </li>
        ))}
      </ul>
      <div className="ml-5 flex items-center justify-between border-t border-border/30 pt-0.5 text-[11px]">
        <span className="font-medium text-muted-foreground">Subtotal</span>
        <span className="tabular-nums font-medium">{copFmt.format(subtotal)}</span>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  muted,
  accent,
}: {
  label: string;
  value: number;
  bold?: boolean;
  muted?: boolean;
  accent?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${
        bold ? "font-semibold" : "font-normal"
      } ${muted ? "text-muted-foreground" : ""} ${accent ? "text-primary" : ""}`}
    >
      <span className="text-xs">{label}</span>
      <span className="tabular-nums text-sm">{copFmt.format(value || 0)}</span>
    </div>
  );
}
